import { Head, router } from "@inertiajs/react"
import type { EnneagramType, ProfileData } from "@/types/vocational"
import { PageLayout } from "@/components/VocationalTest/PageLayout"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { useState } from "react"
import { useConfetti } from "@/hooks/useConfetti"
import jsPDF from "jspdf"
import logo from "@/assets/logo-25segundos-new.png"
import footerImage from "@/assets/metodo-rodape.png"

const ANSWERS_KEY = "basic_answers"

interface ResultsPageProps {
  profiles: ProfileData[]
  results_offer_active?: boolean
  pdf_report_data?: {
    nome?: string | null
    email?: string | null
    idade?: number | null
    telefone?: string | null
    escola?: string | null
    serie?: string | null
    teste_basico_inicio_at?: string | null
    teste_basico_fim_at?: string | null
  } | null
}

function computeCounts() {
  const raw = window.localStorage.getItem(ANSWERS_KEY)
  const counts: Record<EnneagramType, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0 }
  if (!raw) return counts
  const answers: Record<number, EnneagramType> = JSON.parse(raw)
  Object.values(answers).forEach((value) => {
    counts[value] += 1
  })
  return counts
}

function computeDominantType(counts: Record<EnneagramType, number>): EnneagramType | null {
  const sorted = (Object.entries(counts) as [string, number][])
    .map(([type, count]) => [Number(type) as EnneagramType, count] as [EnneagramType, number])
    .sort((a, b) => b[1] - a[1])
  const top = sorted[0]
  return top && top[1] > 0 ? top[0] : null
}

function buildProfileLookup(profiles: ProfileData[]): Record<number, ProfileData> {
  const lookup: Record<number, ProfileData> = {}
  profiles.forEach((p) => { lookup[p.tipo] = p })
  return lookup
}

export default function ResultsPage({ profiles, pdf_report_data, results_offer_active }: ResultsPageProps) {
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false)
  useConfetti()
  const counts = computeCounts()
  const dominantType = computeDominantType(counts)
  const profileLookup = buildProfileLookup(profiles)
  const profileData = dominantType ? profileLookup[dominantType] : null
  const totalAnswered = Object.values(counts).reduce((sum, value) => sum + value, 0)
  const allTypes = (Object.entries(counts) as [string, number][]).map(([type, count]) => ({
    type: Number(type) as EnneagramType,
    name: profileLookup[Number(type)]?.titulo || `Tipo ${type}`,
    count
  }))

  const generatePDF = async () => {
    if (!dominantType || !profileData) {
      return
    }

    setIsGeneratingPDF(true)
    try {
      const doc = new jsPDF()
      const pageWidth = doc.internal.pageSize.getWidth()
      const pageHeight = doc.internal.pageSize.getHeight()
      const pageMargin = 20
      const contentWidth = pageWidth - (2 * pageMargin)
      let yPosition = pageMargin
      const textMaxWidth = contentWidth

      const logoImg = new Image()
      logoImg.src = logo
      await new Promise((resolve) => {
        logoImg.onload = resolve
      })

      const addNewPage = () => {
        doc.addPage()
        yPosition = pageMargin + 15

        const logoWidth = 80
        const logoHeight = (logoImg.height * logoWidth) / logoImg.width
        const logoX = pageMargin + (contentWidth - logoWidth) / 2
        doc.addImage(logoImg, "PNG", logoX, yPosition, logoWidth, logoHeight)
        yPosition += logoHeight + 20
        doc.setTextColor(16, 42, 88)
      }

      doc.setTextColor(16, 42, 88)

      const logoWidth = 80
      const logoHeight = (logoImg.height * logoWidth) / logoImg.width
      const logoX = pageMargin + (contentWidth - logoWidth) / 2
      doc.addImage(logoImg, "PNG", logoX, yPosition, logoWidth, logoHeight)
      yPosition += logoHeight + 20

      doc.setFontSize(24)
      doc.setFont("helvetica", "bold")
      doc.text("Orientação Profissional", pageWidth / 2, yPosition, { align: "center" })
      yPosition += 10
      doc.setFontSize(14)
      doc.setFont("helvetica", "normal")
      doc.text("Eneagrama + Método S.E.M.E.A.R.", pageWidth / 2, yPosition, { align: "center" })
      yPosition += 25

      if (pdf_report_data) {
        doc.setFontSize(12)
        doc.setFont("helvetica", "bold")
        doc.text("Dados do Participante", pageMargin, yPosition)
        yPosition += 8

        doc.setFontSize(10)
        doc.setFont("helvetica", "normal")
        if (pdf_report_data.nome) {
          doc.text(`Nome: ${pdf_report_data.nome}`, pageMargin + 5, yPosition)
          yPosition += 6
        }
        if (pdf_report_data.email) {
          doc.text(`Email: ${pdf_report_data.email}`, pageMargin + 5, yPosition)
          yPosition += 6
        }
        if (pdf_report_data.idade) {
          doc.text(`Idade: ${pdf_report_data.idade} anos`, pageMargin + 5, yPosition)
          yPosition += 6
        }
        if (pdf_report_data.telefone) {
          doc.text(`Telefone: ${pdf_report_data.telefone}`, pageMargin + 5, yPosition)
          yPosition += 6
        }
        if (pdf_report_data.escola && pdf_report_data.escola !== "Externo") {
          doc.text(`Escola: ${pdf_report_data.escola}`, pageMargin + 5, yPosition)
          yPosition += 6
        }
        if (pdf_report_data.serie && pdf_report_data.serie.trim() !== "") {
          doc.text(`Série: ${pdf_report_data.serie}`, pageMargin + 5, yPosition)
          yPosition += 6
        }
        if (pdf_report_data.teste_basico_inicio_at && pdf_report_data.teste_basico_fim_at) {
          const startTime = new Date(pdf_report_data.teste_basico_inicio_at)
          const endTime = new Date(pdf_report_data.teste_basico_fim_at)
          const durationMinutes = Math.round((endTime.getTime() - startTime.getTime()) / 1000 / 60)
          doc.text(`Tempo de conclusão: ${durationMinutes} minutos`, pageMargin + 5, yPosition)
          yPosition += 6
        }

        yPosition += 15
      }

      doc.setFontSize(18)
      doc.setFont("helvetica", "bold")
      doc.text("SEMEIE O QUE DESCOBRIU EM VOCÊ", pageWidth / 2, yPosition, { align: "center" })
      yPosition += 15
      doc.setFontSize(12)
      doc.setFont("helvetica", "normal")
      const congratsText = [
        "Parabéns! Você chegou ao fim da Orientação Profissional 25Segundos.",
        "Cada resposta que você deu foi uma semente do seu autoconhecimento.",
        "Agora é hora de colher os frutos do que você descobriu sobre si mesmo."
      ]
      congratsText.forEach((text) => {
        const lines = doc.splitTextToSize(text, textMaxWidth - 20)
        doc.text(lines, pageWidth / 2, yPosition, { align: "center" })
        yPosition += lines.length * 6 + 4
      })
      yPosition += 20

      if (yPosition > pageHeight - 80) {
        addNewPage()
      }

      doc.setFontSize(36)
      doc.setFont("helvetica", "bold")
      doc.text(`TIPO ${dominantType}`, pageWidth / 2, yPosition, { align: "center" })
      yPosition += 15
      doc.setFontSize(20)
      doc.text(profileData.titulo || `Tipo ${dominantType}`, pageWidth / 2, yPosition, { align: "center" })
      yPosition += 10
      doc.setFontSize(11)
      doc.setFont("helvetica", "italic")
      doc.text("Seu Eneatipo Vocacional", pageWidth / 2, yPosition, { align: "center" })
      yPosition += 25

      if (profileData.descricao_curta) {
        const descLines = doc.splitTextToSize(profileData.descricao_curta, textMaxWidth - 30)
        const blockHeight = descLines.length * 6 + 10
        if (yPosition + blockHeight > pageHeight - 40) {
          addNewPage()
        }
        doc.setFontSize(12)
        doc.setFont("helvetica", "normal")
        doc.text(descLines, pageWidth / 2, yPosition, { align: "center" })
        yPosition += blockHeight + 15
      }

      addNewPage()

      if (profileData.talent) {
        doc.setFontSize(16)
        doc.setFont("helvetica", "bold")
        doc.text("Seu Talento Natural", pageMargin, yPosition)
        yPosition += 10
        const talentLines = doc.splitTextToSize(profileData.talent, textMaxWidth - 10)
        doc.setFontSize(11)
        doc.setFont("helvetica", "normal")
        doc.text(talentLines, pageMargin + 5, yPosition)
        yPosition += talentLines.length * 6 + 18
      }

      if (profileData.style) {
        if (yPosition > pageHeight - 60) {
          addNewPage()
        }
        doc.setFontSize(16)
        doc.setFont("helvetica", "bold")
        doc.text("Seu Estilo de Ação", pageMargin, yPosition)
        yPosition += 10
        const styleLines = doc.splitTextToSize(profileData.style, textMaxWidth - 10)
        doc.setFontSize(11)
        doc.setFont("helvetica", "normal")
        doc.text(styleLines, pageMargin + 5, yPosition)
        yPosition += styleLines.length * 6 + 18
      }

      if (profileData.description) {
        if (yPosition > pageHeight - 70) {
          addNewPage()
        }
        doc.setFontSize(16)
        doc.setFont("helvetica", "bold")
        doc.text("Sobre Você", pageMargin, yPosition)
        yPosition += 10
        const descriptionLines = doc.splitTextToSize(profileData.description, textMaxWidth - 10)
        doc.setFontSize(11)
        doc.setFont("helvetica", "normal")
        doc.text(descriptionLines, pageMargin + 5, yPosition)
        yPosition += descriptionLines.length * 6 + 18
      }

      if (profileData.pontos_fortes) {
        if (yPosition > pageHeight - 70) {
          addNewPage()
        }
        doc.setFontSize(16)
        doc.setFont("helvetica", "bold")
        doc.text("Seus Pontos Fortes", pageMargin, yPosition)
        yPosition += 10
        const pontosLines = doc.splitTextToSize(profileData.pontos_fortes, textMaxWidth - 10)
        doc.setFontSize(11)
        doc.setFont("helvetica", "normal")
        doc.text(pontosLines, pageMargin + 5, yPosition)
        yPosition += pontosLines.length * 6 + 18
      }

      if (profileData.pontos_fracos) {
        if (yPosition > pageHeight - 70) {
          addNewPage()
        }
        doc.setFontSize(16)
        doc.setFont("helvetica", "bold")
        doc.text("Pontos de Atenção e Desenvolvimento", pageMargin, yPosition)
        yPosition += 10
        const atencaoLines = doc.splitTextToSize(profileData.pontos_fracos, textMaxWidth - 10)
        doc.setFontSize(11)
        doc.setFont("helvetica", "normal")
        doc.text(atencaoLines, pageMargin + 5, yPosition)
        yPosition += atencaoLines.length * 6 + 18
      }

      if (profileData.estilos_profissionais) {
        if (yPosition > pageHeight - 70) {
          addNewPage()
        }
        doc.setFontSize(16)
        doc.setFont("helvetica", "bold")
        doc.text("Seus Estilos Profissionais", pageMargin, yPosition)
        yPosition += 10
        const estilosLines = doc.splitTextToSize(profileData.estilos_profissionais, textMaxWidth - 10)
        doc.setFontSize(11)
        doc.setFont("helvetica", "normal")
        doc.text(estilosLines, pageMargin + 5, yPosition)
        yPosition += estilosLines.length * 6 + 18
      }

      if (profileData.beneficios) {
        if (yPosition > pageHeight - 70) {
          addNewPage()
        }
        doc.setFontSize(16)
        doc.setFont("helvetica", "bold")
        doc.text("Benefícios de Conhecer Seu Perfil", pageMargin, yPosition)
        yPosition += 10
        const beneficiosLines = doc.splitTextToSize(profileData.beneficios, textMaxWidth - 10)
        doc.setFontSize(11)
        doc.setFont("helvetica", "normal")
        doc.text(beneficiosLines, pageMargin + 5, yPosition)
        yPosition += beneficiosLines.length * 6 + 18
      }

      const desenvolveText = "Pessoas com seu perfil tendem a se desenvolver melhor em ambientes que respeitam sua forma natural de processar informações e tomar decisões. Compreender seu tipo Eneagrama permite que você:"
      const desenvolveItems = [
        "Identifique contextos profissionais que reduzem ansiedade e aumentam motivação",
        "Reconheça padrões de comportamento que podem estar limitando seu crescimento",
        "Desenvolva estratégias personalizadas para lidar com desafios específicos do seu tipo",
        "Escolha áreas de atuação mais alinhadas com seus talentos e valores naturais",
        "Compreenda melhor suas reações emocionais diante de decisões importantes"
      ]
      if (yPosition > pageHeight - 90) {
        addNewPage()
      }
      doc.setFontSize(16)
      doc.setFont("helvetica", "bold")
      doc.text("Como Você Se Desenvolve Melhor", pageMargin, yPosition)
      yPosition += 10
      const desenvolveIntroLines = doc.splitTextToSize(desenvolveText, textMaxWidth - 10)
      doc.setFontSize(11)
      doc.setFont("helvetica", "normal")
      doc.text(desenvolveIntroLines, pageMargin + 5, yPosition)
      yPosition += desenvolveIntroLines.length * 6 + 8
      desenvolveItems.forEach((item) => {
        if (yPosition > pageHeight - 25) {
          addNewPage()
        }
        const itemLines = doc.splitTextToSize(`• ${item}`, textMaxWidth - 15)
        doc.text(itemLines, pageMargin + 10, yPosition)
        yPosition += itemLines.length * 6 + 2
      })
      yPosition += 15

      addNewPage()
      doc.setFontSize(18)
      doc.setFont("helvetica", "bold")
      doc.text("ENEAGRAMA", pageWidth / 2, yPosition, { align: "center" })
      yPosition += 10
      doc.setFontSize(12)
      doc.setFont("helvetica", "italic")
      doc.text("O Significado", pageWidth / 2, yPosition, { align: "center" })
      yPosition += 18

      const eneagramaText = "O Eneagrama é um mapa de personalidade que descreve nove formas de perceber, sentir, pensar e agir no mundo. Ele revela motivações profundas, padrões emocionais e maneiras de lidar com desafios. Não é um rótulo, mas uma ferramenta de autoconhecimento que ajuda a entender por que fazemos o que fazemos. Seu objetivo é ampliar consciência, oferecer clareza sobre escolhas e fortalecer o desenvolvimento humano."
      const eneagramaLines = doc.splitTextToSize(eneagramaText, textMaxWidth - 10)
      doc.setFontSize(11)
      doc.setFont("helvetica", "normal")
      doc.text(eneagramaLines, pageMargin + 5, yPosition)
      yPosition += eneagramaLines.length * 6 + 20

      const semearApplicabilityText = "O Método S.E.M.E.A.R. se integra ao Eneagrama para criar uma leitura vocacional completa e profundamente humana. Enquanto o Eneagrama revela quem você é por dentro — suas motivações, necessidades emocionais, talentos naturais e desafios — o SEMEAR organiza esse conhecimento em uma jornada prática: Sentir, Entender, Mudar, Escolher, Agir e Realizar. Na orientação profissional, essa integração permite que o estudante compreenda o que sente diante do futuro, reconheça seus padrões internos, identifique bloqueios e fortaleça seu processo de decisão. Em conjunto, Eneagrama e SEMEAR apontam caminhos profissionais mais coerentes com o jeito de ser de cada aluno, mostrando ambientes de trabalho que reduzem ansiedade, carreiras que combinam com seu estilo emocional e áreas que despertam motivação real. Essa união transforma a orientação em um processo de autoconhecimento que guia escolhas acadêmicas e profissionais com mais consciência, segurança e propósito."
      if (yPosition > pageHeight - 100) {
        addNewPage()
      }
      doc.setFontSize(16)
      doc.setFont("helvetica", "bold")
      doc.text("A Aplicabilidade do S.E.M.E.A.R.", pageMargin, yPosition)
      yPosition += 8
      doc.text("+ Eneagrama na Orientação Profissional", pageMargin, yPosition)
      yPosition += 15
      const semearApplicabilityLines = doc.splitTextToSize(semearApplicabilityText, textMaxWidth - 10)
      doc.setFontSize(11)
      doc.setFont("helvetica", "normal")
      doc.text(semearApplicabilityLines, pageMargin + 5, yPosition)
      yPosition += semearApplicabilityLines.length * 6 + 20

      addNewPage()
      doc.setFontSize(16)
      doc.setFont("helvetica", "bold")
      doc.text("Distribuição de Todos os Tipos", pageMargin, yPosition)
      yPosition += 15
      doc.setFontSize(11)
      doc.setFont("helvetica", "normal")
      const totalAnswersAll = allTypes.reduce((sum, item) => sum + item.count, 0)
      allTypes.forEach((typeInfo) => {
        if (yPosition > pageHeight - 25) {
          addNewPage()
        }
        const typeText = `TIPO ${typeInfo.type} | ${typeInfo.name.replace("O ", "")}`
        const countText = `${typeInfo.count} de ${totalAnswersAll}`
        doc.setFont("helvetica", typeInfo.type === dominantType ? "bold" : "normal")
        doc.text(typeText, pageMargin + 5, yPosition)
        doc.text(countText, pageWidth - pageMargin - 5, yPosition, { align: "right" })
        yPosition += 8
      })
      yPosition += 15

      if (yPosition > pageHeight - 70) {
        addNewPage()
      }
      doc.setFontSize(16)
      doc.setFont("helvetica", "bold")
      doc.text("Áreas Profissionais Compatíveis", pageMargin, yPosition)
      yPosition += 15
      doc.setFontSize(11)
      doc.setFont("helvetica", "normal")
      careers.forEach((career) => {
        if (yPosition > pageHeight - 25) {
          addNewPage()
        }
        doc.text(`• ${career}`, pageMargin + 5, yPosition)
        yPosition += 8
      })

      const footerImg = new Image()
      footerImg.src = footerImage
      await new Promise((resolve) => {
        footerImg.onload = resolve
      })
      const totalPages = doc.getNumberOfPages()
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i)
        const footerWidth = pageWidth - 40
        const footerHeight = (footerImg.height * footerWidth) / footerImg.width
        const footerY = pageHeight - footerHeight - 10
        doc.addImage(footerImg, "PNG", 20, footerY, footerWidth, footerHeight)
      }

      const pdfName = `Resultado-Teste-Vocacional-Tipo-${dominantType}.pdf`
      const blobUrl = doc.output("bloburl")
      if (blobUrl) {
        window.open(blobUrl, "_blank", "noopener,noreferrer")
      } else {
        doc.save(pdfName)
      }
    } catch (error) {
      console.error("Erro ao gerar PDF:", error)
    } finally {
      setIsGeneratingPDF(false)
    }
  }

  const careers = (() => {
    if (!profileData?.profissoes_indicadas) return []
    if (Array.isArray(profileData.profissoes_indicadas)) return profileData.profissoes_indicadas
    return profileData.profissoes_indicadas.split("\n").filter(Boolean)
  })()

  const showOffer = results_offer_active !== false

  return (
    <>
      <Head title="Resultados" />
      <PageLayout>
        <div className="w-full max-w-4xl relative">
          <div className="space-y-6">
            <Card className="p-8 md:p-12 shadow-elegant bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5 border-2 border-primary/30 text-center">
              <div className="space-y-4">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-700">
                  SEMEIE O QUE DESCOBRIU EM VOCÊ
                </h2>
                <div className="space-y-3 text-gray-600 leading-relaxed">
                  <p className="text-3xl md:text-4xl font-bold bg-gradient-primary bg-clip-text text-transparent animate-fade-in">
                    ⮯ Parabéns!
                  </p>
                  <p className="text-lg text-left text-justify indent-6">
                    Você chegou ao fim da Orientação Profissional 25Segundos — Eneagrama + Método S.E.M.E.A.R.
                  </p>
                  <p className="text-lg text-left text-justify indent-6">
                    ✧ Cada resposta que você deu foi uma semente do seu autoconhecimento.
                  </p>
                  <p className="text-lg text-left text-justify indent-6">
                    Agora é hora de colher os frutos do que você descobriu sobre si mesmo.
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-8 md:p-12 shadow-elegant border-primary/20">
              <div className="text-center space-y-6 mb-10">
                <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-primary text-white text-6xl font-bold shadow-glow">
                  {dominantType ?? "?"}
                </div>

                <div>
                  <h1 className="text-4xl md:text-5xl font-bold mb-3 pb-2 bg-gradient-primary bg-clip-text text-transparent leading-tight">
                    ✧ {profileData?.titulo || "Seu Eneatipo"}
                  </h1>
                  <p className="text-xl text-muted-foreground font-medium">✺ Seu Eneatipo Vocacional</p>
                  {profileData?.descricao_curta && (
                    <p className="text-lg text-muted-foreground mt-4 max-w-2xl mx-auto leading-relaxed text-left text-justify indent-6">
                      {profileData.descricao_curta}
                    </p>
                  )}
                </div>
              </div>

              {profileData && (
                <div className="space-y-6">
                  <div className="p-6 rounded-lg bg-accent/5 border border-accent/20">
                    <div className="flex items-start gap-3 mb-3">
                      <div>
                        <h3 className="font-semibold text-lg mb-1">✺ Seu Talento Natural</h3>
                        <p className="text-muted-foreground text-left text-justify indent-6">⮕ {profileData.talent}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div>
                        <h3 className="font-semibold text-lg mb-1">✺ Seu Estilo de Ação</h3>
                        <p className="text-muted-foreground text-left text-justify indent-6">⮕ {profileData.style}</p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <h3 className="font-semibold text-2xl mb-4 text-primary">✧ Sobre Você</h3>
                    <div className="p-6 rounded-xl bg-gradient-to-br from-background to-primary/5 border-2 border-primary/20 shadow-md">
                      <p className="text-foreground leading-relaxed text-lg text-left text-justify indent-6">{profileData.description}</p>
                    </div>

                    {profileData.pontos_fortes && (
                      <div className="p-6 rounded-xl bg-gradient-to-br from-primary/10 to-primary/5 border-2 border-primary/20 shadow-md">
                        <h4 className="text-foreground font-bold text-lg mb-3">✺ Seus Pontos Fortes</h4>
                        <p className="text-foreground/90 leading-relaxed text-left text-justify indent-6">
                          {profileData.pontos_fortes}
                        </p>
                      </div>
                    )}

                    {profileData.pontos_fracos && (
                      <div className="p-6 rounded-xl bg-gradient-to-br from-secondary/10 to-secondary/5 border-2 border-secondary/20 shadow-md">
                        <h4 className="text-foreground font-bold text-lg mb-3">✺ Pontos de Atenção e Desenvolvimento</h4>
                        <p className="text-foreground/90 leading-relaxed text-left text-justify indent-6">
                          {profileData.pontos_fracos}
                        </p>
                      </div>
                    )}

                    {profileData.estilos_profissionais && (
                      <div className="p-6 rounded-xl bg-gradient-to-br from-accent/10 to-accent/5 border-2 border-accent/20 shadow-md">
                        <h4 className="text-foreground font-bold text-lg mb-3">✺ Seus Estilos Profissionais</h4>
                        <p className="text-foreground/90 leading-relaxed text-left text-justify indent-6">
                          {profileData.estilos_profissionais}
                        </p>
                      </div>
                    )}

                    {profileData.beneficios && (
                      <div className="p-6 rounded-xl bg-gradient-to-br from-success/10 to-success/5 border-2 border-success/20 shadow-md">
                        <h4 className="text-foreground font-bold text-lg mb-3">✺ Benefícios de Conhecer seu Tipo</h4>
                        <p className="text-foreground/90 leading-relaxed text-left text-justify indent-6">
                          {profileData.beneficios}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="p-6 rounded-lg bg-secondary/5 border border-secondary/20">
                    <h3 className="font-semibold text-xl mb-3">✧ ENEAGRAMA SIGNIFICADO</h3>
                    <p className="text-muted-foreground leading-relaxed text-left text-justify indent-6">
                      O Eneagrama é um mapa de personalidade que descreve nove formas de perceber, sentir, pensar e agir no mundo. Ele revela motivações profundas, padrões emocionais e maneiras de lidar com desafios. Não é um rótulo, mas uma ferramenta de autoconhecimento que ajuda a entender por que fazemos o que fazemos. Seu objetivo é ampliar consciência, oferecer clareza sobre escolhas e fortalecer o desenvolvimento humano.
                    </p>
                  </div>

                  <div className="p-6 rounded-lg bg-accent/5 border border-accent/20">
                    <h3 className="font-semibold text-xl mb-3">✧ A APLICABILIDADE DO S.E.M.E.A.R. + ENEAGRAMA NA ORIENTAÇÃO PROFISSIONAL</h3>
                    <p className="text-muted-foreground leading-relaxed text-left text-justify indent-6">
                      O Método S.E.M.E.A.R. se integra ao Eneagrama para criar uma leitura vocacional completa e profundamente humana. Enquanto o Eneagrama revela quem você é por dentro — suas motivações, necessidades emocionais, talentos naturais e desafios — o SEMEAR organiza esse conhecimento em uma jornada prática: Sentir, Entender, Mudar, Escolher, Agir e Realizar.
                    </p>
                    <p className="text-muted-foreground leading-relaxed mt-3 text-left text-justify indent-6">
                      Na orientação profissional, essa integração permite que o estudante compreenda o que sente diante do futuro, reconheça seus padrões internos, identifique bloqueios e fortaleça seu processo de decisão. Em conjunto, Eneagrama e SEMEAR apontam caminhos profissionais mais coerentes com o jeito de ser de cada aluno, mostrando ambientes de trabalho que reduzem ansiedade, carreiras que combinam com seu estilo emocional e áreas que despertam motivação real.
                    </p>
                    <p className="text-muted-foreground leading-relaxed mt-3 text-left text-justify indent-6">
                      Essa união transforma a orientação em um processo de autoconhecimento que guia escolhas acadêmicas e profissionais com mais consciência, segurança e propósito.
                    </p>
                  </div>

                  <div>
                    <h3 className="font-semibold text-xl mb-4">✧ Distribuição de Todos os Tipos</h3>
                    <div className="space-y-2">
                      {(Object.entries(counts) as [string, number][]).map(([type, count]) => {
                        const typeNum = Number(type) as EnneagramType
                        const name = profileLookup[typeNum]?.titulo?.replace("O ", "")
                        return (
                          <div
                            key={type}
                            className={
                              typeNum === dominantType
                                ? "flex items-center justify-between p-4 rounded-lg border-2 bg-primary/10 border-primary/40 shadow-md"
                                : "flex items-center justify-between p-4 rounded-lg border-2 bg-muted/30 border-muted"
                            }
                          >
                            <span className="font-bold text-base">TIPO {type} | {name}</span>
                            <span className="text-lg font-semibold">{count} de {totalAnswered}</span>
                          </div>
                        )
                      })}
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold text-xl mb-4">✧ Áreas Profissionais Compatíveis</h3>
                    <div className="grid md:grid-cols-2 gap-3">
                      {careers.map((career, index) => (
                        <div key={index} className="p-3 rounded-lg bg-primary/5 border border-primary/10 text-sm">
                          ⮕ {career}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-10 pt-10 border-t-2 border-primary/20 space-y-6">
                <div className="text-center space-y-4">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-primary mb-2">
                    <span className="text-white text-2xl">✦</span>
                  </div>

                  {showOffer && (
                    <>
                      <h3 className="font-bold text-2xl md:text-3xl">✧ Quer Aprofundar seu Autoconhecimento?</h3>
                      <p className="text-muted-foreground text-lg max-w-2xl mx-auto leading-relaxed">
                        ⮕ Esta descoberta é apenas o primeiro passo. Desbloqueie todo o potencial do seu Eneatipo e transforme sua jornada profissional.
                      </p>
                    </>
                  )}

                  <div className="flex flex-col gap-4 items-center mt-6">
                    <Button
                      onClick={generatePDF}
                      size="lg"
                      disabled={isGeneratingPDF}
                      className="bg-gradient-primary hover:opacity-90 transition-all text-white text-lg px-10 py-7 h-auto shadow-glow font-bold rounded-xl hover:scale-105"
                    >
                      {isGeneratingPDF ? "Gerando PDF..." : "📄 Gerar PDF do Meu Resultado"}
                    </Button>

                    {showOffer && (
                      <div className="flex flex-col sm:flex-row gap-3 w-full max-w-lg">
                        <Button
                          onClick={() => router.visit("/checkout")}
                          size="lg"
                          className="flex-1 bg-[#ED7723] hover:bg-[#ED7723]/90 text-white text-base px-6 py-6 h-auto font-bold rounded-xl hover:scale-105"
                        >
                          OFERTA ESPECIAL →
                        </Button>
                        <Button
                          onClick={() => router.visit("/")}
                          size="lg"
                          variant="outline"
                          className="flex-1 text-base px-6 py-6 h-auto font-semibold rounded-xl hover:scale-105"
                        >
                          Nesse momento me contento com esse resultado
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </PageLayout>
    </>
  )
}
